import React, { useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import { LoadingFeedback } from 'components'
import {
  FlowchartHeader,
  FlowchartContainer,
  FlowchartFullscreenDialog,
  FlowchartUtils,
} from './components'

import useStyles from './styles'

const Flowchart = ({
  data = [],
  title = 'Fluxograma',
  description = 'Visualização do fluxo',
}) => {
  /**
   * jsDoc
   * @param {Object} props - Propriedades do componente.
   * @param {Array} props.data - Array de objetos.
   * @param {object} props.data[].children - Elementos filhos do elemento do fluxograma. Todos os parâmetros são opcionais tirando o name e as opções disponíveis são os mesmo do elemento pai.
   * @param {string} [props.data[].name = 'Nome do elemento'] - Nome do elemento do fluxograma.
   * @param {string} [props.data[].textColor = 'Cor do texto'] - Cor do texto do elemento do fluxograma.
   * @param {string} [props.data[].color = 'Cor da corda e da ligacao entre elementos'] - Cor do elemento do fluxograma.
   * @param {string} [props.data[].type = 'Tipo do elemento, pode ser event, annotation ou data.'] - Tipo do elemento do fluxograma.
   * @param {string} [props.title = 'Fluxograma'] - Título do fluxograma.
   * @param {string} [props.description = 'Visualização do fluxo'] - Descrição do fluxograma.
   */

  const classes = useStyles()
  const flowchartRef = useRef(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const { renderElements, organizeElementsByLevel } = FlowchartUtils()

  const generatePDF = async () => {
    if (!flowchartRef.current) return
    setIsLoading(true)

    try {
      const canvas = await html2canvas(flowchartRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: flowchartRef.current.scrollWidth,
        height: flowchartRef.current.scrollHeight,
      })

      const imgData = canvas.toDataURL('image/png')

      const pdfWidth = 297
      const pdfHeight = 210

      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)

      const imgWidthScaled = imgWidth * ratio
      const imgHeightScaled = imgHeight * ratio

      const pdf = new jsPDF('landscape', 'mm', 'a4')

      const x = (pdfWidth - imgWidthScaled) / 2
      const y = (pdfHeight - imgHeightScaled) / 2

      pdf.addImage(imgData, 'PNG', x, y, imgWidthScaled, imgHeightScaled)

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
      const fileName = `fluxograma-${timestamp}.pdf`

      pdf.save(fileName)
    } catch (error) {
      console.error('Erro ao gerar PDF:', error)
      alert('Erro ao gerar PDF. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const toggleFullscreen = () =>
    setIsFullscreen((isFullscreen) => !isFullscreen)

  if (!data || data.length === 0) {
    return (
      <div className={classes.box}>
        <div className={classes.boxText}>Nenhum dado fornecido</div>
      </div>
    )
  }

  const elements = renderElements(data)
  const elementsByLevel = organizeElementsByLevel(elements)

  return (
    <div className={classes.root}>
      <LoadingFeedback open={isLoading} />

      <FlowchartHeader
        title={title}
        description={description}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onDownloadPDF={generatePDF}
      />
      <FlowchartContainer
        elementsByLevel={elementsByLevel}
        isFullscreenMode={false}
        flowchartRef={flowchartRef}
      />

      <FlowchartFullscreenDialog
        open={isFullscreen}
        onClose={toggleFullscreen}
        title={title}
        elementsByLevel={elementsByLevel}
      />
    </div>
  )
}

export default Flowchart
